import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import realtimePresenceService from '../../../services/realtimePresenceService';
import { useAuth } from '../../../contexts/AuthContext';

const PartnerPresenceIndicator = ({ partnerName = 'Partner', isConnected = false }) => {
  const { userProfile } = useAuth();
  const [presenceStatus, setPresenceStatus] = useState('offline');
  const [connectionQuality, setConnectionQuality] = useState('excellent');
  const [moodSyncStatus, setMoodSyncStatus] = useState('synced');
  const [lastSeen, setLastSeen] = useState('2 minutes ago');
  const [isPulsing, setIsPulsing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Real-time presence subscription
  useEffect(() => {
    if (!isConnected || !userProfile?.partner_id) {
      setPresenceStatus('offline');
      setIsLoading(false);
      return;
    }

    const partnerId = userProfile?.partner_id;

    // Fetch initial presence data
    const loadInitialPresence = async () => {
      setIsLoading(true);
      const presenceData = await realtimePresenceService?.fetchPartnerPresence(partnerId);
      
      if (presenceData) {
        setPresenceStatus(presenceData?.status || 'offline');
        setConnectionQuality(presenceData?.connectionQuality || 'good');
        setLastSeen(realtimePresenceService?.getTimeSinceLastSeen(presenceData?.lastSeenAt));
        
        // Determine mood sync status
        if (presenceData?.currentMood && presenceData?.moodSyncedAt) {
          const syncAge = new Date() - new Date(presenceData?.moodSyncedAt);
          setMoodSyncStatus(syncAge < 60000 ? 'synced' : 'pending'); // synced if < 1 min old
        } else {
          setMoodSyncStatus('pending');
        }

        // Trigger pulse animation for online status
        if (presenceData?.status === 'online') {
          setIsPulsing(true);
          setTimeout(() => setIsPulsing(false), 2000);
        }
      }
      setIsLoading(false);
    };

    loadInitialPresence();

    // Subscribe to real-time presence updates
    const channel = realtimePresenceService?.subscribeToPartnerPresence(
      partnerId,
      (presenceData) => {
        // Update presence status
        setPresenceStatus(presenceData?.status || 'offline');
        setConnectionQuality(presenceData?.connectionQuality || 'good');
        setLastSeen(realtimePresenceService?.getTimeSinceLastSeen(presenceData?.lastSeenAt));
        
        // Update mood sync status
        if (presenceData?.currentMood && presenceData?.moodSyncedAt) {
          const syncAge = new Date() - new Date(presenceData?.moodSyncedAt);
          if (syncAge < 5000) {
            // Just synced (< 5 seconds ago)
            setMoodSyncStatus('syncing');
            setTimeout(() => setMoodSyncStatus('synced'), 1500);
          } else {
            setMoodSyncStatus('synced');
          }
        } else {
          setMoodSyncStatus('pending');
        }

        // Trigger pulse animation for online status changes
        if (presenceData?.status === 'online') {
          setIsPulsing(true);
          setTimeout(() => setIsPulsing(false), 2000);
        }
      }
    );

    // Cleanup subscription on unmount
    return () => {
      if (channel) {
        realtimePresenceService?.unsubscribe(`partner_presence_${partnerId}`);
      }
    };
  }, [isConnected, userProfile?.partner_id]);

  const getPresenceConfig = () => {
    switch (presenceStatus) {
      case 'online':
        return {
          color: 'var(--color-success)',
          bgColor: 'bg-success/10',
          borderColor: 'border-success/30',
          label: 'Online',
          icon: 'Circle',
          description: 'Available now'
        };
      case 'away':
        return {
          color: 'var(--color-warning)',
          bgColor: 'bg-warning/10',
          borderColor: 'border-warning/30',
          label: 'Away',
          icon: 'Clock',
          description: 'Idle for a while'
        };
      case 'busy':
        return {
          color: 'var(--color-destructive)',
          bgColor: 'bg-destructive/10',
          borderColor: 'border-destructive/30',
          label: 'Busy',
          icon: 'MinusCircle',
          description: 'Do not disturb'
        };
      default:
        return {
          color: 'var(--color-muted-foreground)',
          bgColor: 'bg-muted',
          borderColor: 'border-border',
          label: 'Offline',
          icon: 'CircleOff',
          description: lastSeen
        };
    }
  };

  const getConnectionQualityConfig = () => {
    switch (connectionQuality) {
      case 'excellent':
        return {
          color: 'var(--color-success)',
          bars: 4,
          label: 'Excellent',
          description: 'Strong connection'
        };
      case 'good':
        return {
          color: 'var(--color-accent)',
          bars: 3,
          label: 'Good',
          description: 'Stable connection'
        };
      case 'fair':
        return {
          color: 'var(--color-warning)',
          bars: 2,
          label: 'Fair',
          description: 'Weak connection'
        };
      default:
        return {
          color: 'var(--color-destructive)',
          bars: 1,
          label: 'Poor',
          description: 'Unstable connection'
        };
    }
  };

  const getMoodSyncConfig = () => {
    switch (moodSyncStatus) {
      case 'synced':
        return {
          color: 'var(--color-success)',
          icon: 'CheckCircle',
          label: 'Synced',
          description: 'Mood data up to date'
        };
      case 'syncing':
        return {
          color: 'var(--color-accent)',
          icon: 'RefreshCw',
          label: 'Syncing',
          description: 'Updating mood data',
          animate: true
        };
      default:
        return {
          color: 'var(--color-warning)',
          icon: 'AlertCircle',
          label: 'Pending',
          description: 'Waiting for update'
        };
    }
  };

  const presenceConfig = getPresenceConfig();
  const qualityConfig = getConnectionQualityConfig();
  const moodConfig = getMoodSyncConfig();

  if (!isConnected) {
    return (
      <div className="bg-muted border border-border rounded-lg p-4">
        <div className="flex items-center space-x-3">
          <div className="relative">
            <div className="w-12 h-12 rounded-full bg-muted-foreground/10 flex items-center justify-center">
              <Icon name="UserX" size={24} color="var(--color-muted-foreground)" />
            </div>
          </div>
          <div className="flex-1">
            <p className="text-sm font-medium text-muted-foreground">
              Partner not connected yet
            </p>
            <p className="text-xs text-muted-foreground/70">
              Complete verification to see live presence
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="bg-card border border-border rounded-lg p-4 md:p-5 shadow-sm">
        <div className="flex items-center space-x-3">
          <div className="w-12 h-12 rounded-full bg-muted animate-pulse" />
          <div className="flex-1 space-y-2">
            <div className="h-4 bg-muted rounded animate-pulse" />
            <div className="h-3 bg-muted rounded w-2/3 animate-pulse" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg p-4 md:p-5 shadow-sm">
      {/* Header with Partner Name and Main Status */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className="relative">
            {/* Avatar with pulse animation */}
            <div className={`w-12 h-12 rounded-full ${presenceConfig?.bgColor} flex items-center justify-center border-2 ${presenceConfig?.borderColor} relative`}>
              <Icon name="User" size={24} color={presenceConfig?.color} />
              
              {/* Pulse rings for online status */}
              {presenceStatus === 'online' && isPulsing && (
                <>
                  <span className="absolute inset-0 rounded-full bg-success/30 animate-ping" />
                  <span className="absolute inset-0 rounded-full bg-success/20 animate-pulse" style={{ animationDelay: '0.2s' }} />
                </>
              )}
            </div>
            
            {/* Status indicator dot */}
            <div className={`absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full border-2 border-card ${presenceStatus === 'online' ? 'bg-success' : presenceStatus === 'away' ? 'bg-warning' : presenceStatus === 'busy' ? 'bg-destructive' : 'bg-muted-foreground'}`}>
              {presenceStatus === 'online' && (
                <span className="absolute inset-0 rounded-full bg-success animate-pulse" />
              )}
            </div>
          </div>
          
          <div>
            <h3 className="text-base font-semibold text-foreground">
              {partnerName}
            </h3>
            <div className="flex items-center space-x-1.5">
              <Icon name={presenceConfig?.icon} size={12} color={presenceConfig?.color} />
              <span className="text-xs font-medium" style={{ color: presenceConfig?.color }}>
                {presenceConfig?.label}
              </span>
              <span className="text-xs text-muted-foreground">•</span>
              <span className="text-xs text-muted-foreground">
                {presenceConfig?.description}
              </span>
            </div>
          </div>
        </div>
      </div>
      {/* Connection Quality and Mood Sync Status */}
      <div className="grid grid-cols-2 gap-3">
        {/* Connection Quality */}
        <div className="bg-background rounded-lg p-3 border border-border">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium text-muted-foreground">
              Connection
            </span>
            <div className="flex items-center space-x-0.5">
              {[1, 2, 3, 4]?.map((bar) => (
                <div
                  key={bar}
                  className={`w-1 rounded-full transition-all ${
                    bar <= qualityConfig?.bars ? 'opacity-100' : 'opacity-20'
                  }`}
                  style={{
                    height: `${bar * 3 + 4}px`,
                    backgroundColor: bar <= qualityConfig?.bars ? qualityConfig?.color : 'var(--color-muted-foreground)'
                  }}
                />
              ))}
            </div>
          </div>
          <p className="text-xs font-semibold text-foreground">
            {qualityConfig?.label}
          </p>
          <p className="text-xs text-muted-foreground mt-0.5">
            {qualityConfig?.description}
          </p>
        </div>

        {/* Mood Sync Status */}
        <div className="bg-background rounded-lg p-3 border border-border">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium text-muted-foreground">
              Mood Sync
            </span>
            <Icon 
              name={moodConfig?.icon} 
              size={14} 
              color={moodConfig?.color}
              className={moodConfig?.animate ? 'animate-spin' : ''}
            />
          </div>
          <p className="text-xs font-semibold text-foreground">
            {moodConfig?.label}
          </p>
          <p className="text-xs text-muted-foreground mt-0.5">
            {moodConfig?.description}
          </p>
        </div>
      </div>
      {/* Live Activity Indicator */}
      {presenceStatus === 'online' && (
        <div className="mt-3 bg-success/5 border border-success/20 rounded-lg p-2.5">
          <div className="flex items-center space-x-2">
            <div className="relative flex items-center justify-center">
              <span className="absolute w-2 h-2 bg-success rounded-full animate-ping" />
              <span className="relative w-2 h-2 bg-success rounded-full" />
            </div>
            <p className="text-xs text-foreground">
              <span className="font-medium">Live now</span>
              <span className="text-muted-foreground"> • Active in the app</span>
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default PartnerPresenceIndicator;